import { api } from '@/lib/api-client'
import type {
  ChangeRequestField,
  ChangeRequestStatus,
} from '@/shared/api/usersSharedApi'

// Admin-side change-request queue. Backend hydrates the requester user
// info on every row so admin can decide without a second round-trip.

export interface AdminChangeRequestRequester {
  id: string
  display_name: string
  email: string | null
  role: string
}

export interface AdminChangeRequest {
  id: string
  field: ChangeRequestField
  current_value: string | null
  new_value: string
  status: ChangeRequestStatus
  requested_at: string
  decided_at: string | null
  decided_by: { id: string; display_name: string } | null
  rejection_reason: string | null
  user: AdminChangeRequestRequester
}

export interface ListAdminChangeRequestsParams {
  status?: ChangeRequestStatus
  page?: number
  page_size?: number
}

export interface AdminChangeRequestsResponse {
  items: AdminChangeRequest[]
  total_count: number
  page: number
  page_size: number
}

export function listAdminChangeRequests(
  params?: ListAdminChangeRequestsParams,
): Promise<AdminChangeRequestsResponse> {
  return api
    .get<AdminChangeRequestsResponse | AdminChangeRequest[]>(
      '/admin/change-requests',
      { params },
    )
    .then((d) => {
      // Defensive: backend may return either an envelope or a raw array.
      if (Array.isArray(d)) {
        return {
          items: d,
          total_count: d.length,
          page: 1,
          page_size: d.length,
        }
      }
      return d
    })
}

export function approveChangeRequest(id: string): Promise<AdminChangeRequest> {
  return api.post<AdminChangeRequest>(
    `/admin/change-requests/${id}/approve`,
    undefined,
    { successMessage: 'Change approved and applied.', suppressErrorToast: true },
  )
}

export interface RejectChangeRequestBody {
  reason: string
}

export function rejectChangeRequest(
  id: string,
  body: RejectChangeRequestBody,
): Promise<AdminChangeRequest> {
  return api.post<AdminChangeRequest>(
    `/admin/change-requests/${id}/reject`,
    body,
    { successMessage: 'Change request rejected.', suppressErrorToast: true },
  )
}
